import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Search, ShoppingBag, Heart } from 'lucide-react'
import { Logo } from '@/components/layout/Logo'
import { StoreScrollLink } from '@/store/components/StoreScrollLink'
import { useCartStore } from '@/store/stores/cartStore'
import { useWishlistStore } from '@/store/stores/wishlistStore'
import { cn } from '@/lib/utils'

const nav = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Shop' },
  { to: '/shop?sort=new', label: 'New Arrivals' },
  { to: '/contact', label: 'Contact' },
] as const

function navItemClass(active: boolean) {
  return cn(
    'text-sm font-medium transition-colors',
    active ? 'text-gold-300' : 'text-zinc-400 hover:text-gold-200'
  )
}

function mobileNavItemClass(active: boolean) {
  return cn(
    'block text-sm py-2 transition-colors',
    active ? 'font-medium text-gold-300' : 'text-zinc-300 hover:text-gold-200'
  )
}

function useStoreNavActive() {
  const { pathname, search } = useLocation()
  const params = new URLSearchParams(search)
  const sort = params.get('sort')
  const isProductDetail = /^\/shop\/[^/]+$/.test(pathname)

  return {
    isHome: pathname === '/',
    isShop:
      (pathname === '/shop' && sort !== 'new') || isProductDetail,
    isNewArrivals: pathname === '/shop' && sort === 'new',
    isContact: pathname === '/contact',
    isSearch: pathname === '/search',
    isOrders: pathname === '/orders' || pathname.startsWith('/orders/'),
    isWishlist: pathname === '/wishlist',
    isCart: pathname === '/cart',
  }
}

function isNavItemActive(
  to: string,
  active: ReturnType<typeof useStoreNavActive>
): boolean {
  switch (to) {
    case '/':
      return active.isHome
    case '/shop':
      return active.isShop
    case '/shop?sort=new':
      return active.isNewArrivals
    case '/contact':
      return active.isContact
    default:
      return false
  }
}

export function StoreHeader() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const active = useStoreNavActive()
  const itemCount = useCartStore((s) => s.itemCount())
  const wishCount = useWishlistStore((s) => s.ids.length)

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (search.trim()) {
      navigate(`/search?q=${encodeURIComponent(search.trim())}`)
    } else {
      navigate('/search')
    }
  }

  const closeMobileMenu = () => setOpen(false)

  return (
    <header className="sticky top-0 z-50 glass-panel border-b border-luxury-700/50">
      <div className="store-container flex h-16 items-center justify-between gap-4">
        <Logo
          size="sm"
          variant="store"
          showText
          gradientBorder
          linkTo="/"
        />

        <nav className="hidden md:flex items-center gap-8">
          {nav.map(({ to, label }) => {
            const isActive = isNavItemActive(to, active)
            return (
              <StoreScrollLink
                key={to}
                to={to}
                aria-current={isActive ? 'page' : undefined}
                className={navItemClass(isActive)}
              >
                {label}
              </StoreScrollLink>
            )
          })}
        </nav>

        <form onSubmit={onSearch} className="hidden lg:flex flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              className="input-luxury pl-10 py-2"
              placeholder="Search attars..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </form>

        <div className="flex items-center gap-1 sm:gap-2">
          <Link
            to="/search"
            aria-current={active.isSearch ? 'page' : undefined}
            className={cn('btn-ghost p-2.5', active.isSearch && 'text-gold-300')}
            aria-label="Search products"
          >
            <Search className="h-5 w-5" />
          </Link>
          <Link
            to="/wishlist"
            aria-current={active.isWishlist ? 'page' : undefined}
            className={cn(
              'relative btn-ghost p-2.5',
              active.isWishlist && 'text-gold-300'
            )}
          >
            <Heart className="h-5 w-5" />
            {wishCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold-500 text-[10px] font-bold text-luxury-950">
                {wishCount}
              </span>
            )}
          </Link>
          <Link
            to="/cart"
            aria-current={active.isCart ? 'page' : undefined}
            className={cn(
              'relative btn-ghost p-2.5',
              active.isCart && 'text-gold-300'
            )}
          >
            <ShoppingBag className="h-5 w-5" />
            {itemCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold-500 text-[10px] font-bold text-luxury-950">
                {itemCount}
              </span>
            )}
          </Link>
          <button
            className="btn-ghost p-2.5 md:hidden"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t border-luxury-700/50 overflow-hidden"
          >
            <div className="store-container py-4 space-y-3">
              {nav.map(({ to, label }) => {
                const isActive = isNavItemActive(to, active)
                return (
                  <StoreScrollLink
                    key={to}
                    to={to}
                    onClick={closeMobileMenu}
                    aria-current={isActive ? 'page' : undefined}
                    className={mobileNavItemClass(isActive)}
                  >
                    {label}
                  </StoreScrollLink>
                )
              })}
              <StoreScrollLink
                to="/search"
                onClick={closeMobileMenu}
                aria-current={active.isSearch ? 'page' : undefined}
                className={mobileNavItemClass(active.isSearch)}
              >
                Search
              </StoreScrollLink>
              <StoreScrollLink
                to="/orders"
                onClick={closeMobileMenu}
                aria-current={active.isOrders ? 'page' : undefined}
                className={mobileNavItemClass(active.isOrders)}
              >
                My Orders
              </StoreScrollLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
